"use client"

import { useTranslation } from "react-i18next"
import { MessageCircle, Instagram, Facebook, TrendingUp } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { WhatsAppConnection } from "./WhatsAppConnection"
import { WhatsAppNumbersList } from "./WhatsAppNumbersList"
import { InstagramConnection } from "./InstagramConnection"
import { MessengerConnection } from "./MessengerConnection"
import { FacebookAdsLeadsConnection } from "./FacebookAdsLeadsConnection"
import { META_INTEGRATION_ENABLED } from "@/config/env"

interface MetaChannelsSettingsProps {
  translations?: Record<string, unknown>
}

export function MetaChannelsSettings({ translations }: MetaChannelsSettingsProps) {
  const { t } = useTranslation()

  const getTranslation = (key: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : key
    }
    return t(key)
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{getTranslation('settings.meta_channels.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {getTranslation('settings.meta_channels.description')}
        </p>
      </div>
      {!META_INTEGRATION_ENABLED && (
        <div className="rounded-md border border-warning bg-warning px-4 py-3 text-sm text-warning">
          Meta channels are disabled in this environment.
        </div>
      )}

      <div className={`space-y-4 ${!META_INTEGRATION_ENABLED ? 'opacity-60 pointer-events-none' : ''}`}>
        {/* WhatsApp Card */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="min-w-0">{getTranslation('settings.whatsapp.title')}</span>
            </CardTitle>
            <CardDescription>
              {getTranslation('settings.whatsapp.card_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4">
            <WhatsAppConnection translations={translations} />
            <WhatsAppNumbersList />
          </CardContent>
        </Card>

        {/* Instagram Card */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Instagram className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="min-w-0">{getTranslation('settings.instagram.title')}</span>
            </CardTitle>
            <CardDescription>
              {getTranslation('settings.instagram.card_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0">
            <InstagramConnection translations={translations} />
          </CardContent>
        </Card>

        {/* Messenger Card */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="min-w-0">{getTranslation('settings.messenger.title')}</span>
            </CardTitle>
            <CardDescription>
              {getTranslation('settings.messenger.card_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0">
            <MessengerConnection translations={translations} />
          </CardContent>
        </Card>

        {/* Facebook Ads & Leads Card */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="min-w-0">{getTranslation('settings.facebook_ads.title') || 'Facebook Ads & Leads'}</span>
            </CardTitle>
            <CardDescription>
              {getTranslation('settings.facebook_ads.card_description') || 'Connect your Facebook account to access ads campaigns and leads data.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0">
            <FacebookAdsLeadsConnection translations={translations} />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

